import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { useState, useEffect } from "react";
import { 
  Cookie, 
  Shield, 
  Bluetooth, 
  CreditCard, 
  ExternalLink, 
  Keyboard, 
  FileText, 
  MessageCircle, 
  MapPin 
} from "lucide-react";


interface RiskFactor {
  icon: any;
  label: string;
  count: number;
  progress: number;
  link: string;
  riskLevel?: 'low' | 'medium' | 'high';
  description?: string;
}

export function RiskFactors() {
  const [riskFactors, setRiskFactors] = useState<RiskFactor[]>([]);

  // Fetch real scan data
  const { data: scans } = useQuery({
    queryKey: ["/api/scans"],
    refetchInterval: 30000
  });

  const { data: trackers } = useQuery({
    queryKey: ["/api/trackers"],
    refetchInterval: 30000
  });

  // Calculate real risk factors from scan data
  useEffect(() => {
    // Handle case when no data is available for new users
    if (!scans || scans.length === 0) {
      setRiskFactors([
        {
          icon: Shield,
          label: "No Risk Data Available",
          count: 0,
          progress: 0,
          link: "/"
        }
      ]);
      return;
    }

    // Whitelist of legitimate services that should not be counted as risks
    const legitimateServices = [
      // Major CDNs and trusted services
      'google.com', 'googleapis.com', 'gstatic.com', 'googletagmanager.com',
      'jquery.com', 'bootstrap.com', 'cloudflare.com', 'jsdelivr.net',
      'unpkg.com', 'cdnjs.cloudflare.com', 'fonts.googleapis.com',
      'ajax.googleapis.com', 'code.jquery.com',
      // Payment processors (legitimate)
      'stripe.com', 'paypal.com', 'square.com', 'braintreegateway.com',
      'authorize.net', 'worldpay.com', 'adyen.com',
      // Analytics (common legitimate services)
      'google-analytics.com', 'googletagmanager.com', 'facebook.com',
      // Maps (legitimate)
      'maps.googleapis.com', 'mapbox.com', 'leafletjs.com'
    ];

    let cookieTracking = 0;
    let untrustedScripts = 0;
    let openCdnScripts = 0;
    let sensitiveInputs = 0;
    let personalData = 0;
    let maliciousCommunication = 0;
    let bluetoothAccess = 0;
    let paymentFrames = 0;
    let gpsLocation = 0;

    // Helper function to check if a script is from a legitimate service
    const isLegitimateService = (src: string): boolean => {
      if (!src) return false;
      return legitimateServices.some(service => src.includes(service));
    };

    // Count trackers from tracker data for cookies/pixels (only suspicious ones)
    if (trackers?.trackers && Array.isArray(trackers.trackers)) {
      // Only count trackers that aren't from legitimate services
      cookieTracking = trackers.trackers.filter((t: any) => 
        t.domain && !isLegitimateService(t.domain)
      ).length;
    } else if (trackers && Array.isArray(trackers)) {
      cookieTracking = trackers.filter((t: any) => 
        t.domain && !isLegitimateService(t.domain)
      ).length;
    }

    // Also count pixel/tracking scripts directly from scan data (only suspicious)
    let additionalPixelScripts = 0;

    if (scans && Array.isArray(scans)) {
      scans.forEach((scan: any) => {
        try {
          const scanData = typeof scan.results === 'string' ? JSON.parse(scan.results) : scan.results;
          
          if (scanData?.scripts) {
            // Count truly untrusted scripts (excluding trusted CDNs and common services)
            const trustedDomains = [
              'google.com', 'googleapis.com', 'gstatic.com', 'googletagmanager.com',
              'jquery.com', 'bootstrap.com', 'cloudflare.com', 'jsdelivr.net',
              'unpkg.com', 'cdnjs.cloudflare.com', 'fonts.googleapis.com',
              'ajax.googleapis.com', 'code.jquery.com'
            ];
            
            const untrustedOnlyScripts = scanData.scripts.filter((s: any) => {
              if (!s.src) return false;
              try {
                const scriptDomain = new URL(s.src).hostname;
                const scanDomain = new URL(scanData.url || scan.url).hostname;
                
                // Same domain = trusted
                if (scriptDomain === scanDomain) return false;
                
                // Check if it's from a trusted domain
                const isTrustedDomain = trustedDomains.some(trusted => 
                  scriptDomain.includes(trusted)
                );
                
                return !isTrustedDomain;
              } catch {
                return s.isThirdParty === true;
              }
            });
            untrustedScripts += untrustedOnlyScripts.length;
            
            // Count scripts from CDNs (only if not from trusted CDNs)
            const cdnScripts = scanData.scripts.filter((s: any) => {
              if (!s.src) return false;
              const isCdn = s.src.includes('cdn') || 
                           s.src.includes('jsdelivr') || 
                           s.src.includes('unpkg') ||
                           s.src.includes('cloudflare') ||
                           s.src.includes('bootstrap') ||
                           s.src.includes('ajax.googleapis');
              // Only count if it's NOT from a legitimate service
              return isCdn && !isLegitimateService(s.src);
            });
            openCdnScripts += cdnScripts.length;

            // Count ACTUAL form inputs from scan data (not scripts that might access them)
            // Check requestParameters for form inputs
            const requestParameters = scanData.requestParameters || [];
            const formInputsFromParams = requestParameters.filter((param: any) => 
              param.source === 'form' || param.resourceType === 'form'
            );
            
            // Also check forms array
            const forms = scanData.forms || [];
            let formInputsFromForms = 0;
            forms.forEach((form: any) => {
              formInputsFromForms += (form.inputs || []).length;
            });
            
            // Count sensitive inputs (password, email, tel, credit card, etc.)
            const sensitiveInputTypes = ['password', 'email', 'tel', 'number', 'creditcard', 'cvv', 'ssn'];
            const sensitiveCount = formInputsFromParams.filter((param: any) => {
              const type = (param.type || '').toLowerCase();
              const name = (param.name || '').toLowerCase();
              return sensitiveInputTypes.some(sensitive => type.includes(sensitive) || name.includes(sensitive)) ||
                     name.includes('password') || name.includes('email') || name.includes('phone') ||
                     name.includes('credit') || name.includes('card') || name.includes('cvv') ||
                     name.includes('ssn') || name.includes('social');
            }).length;
            
            // Add all form inputs (they're all potentially sensitive)
            sensitiveInputs += Math.max(formInputsFromParams.length, formInputsFromForms);
            
            // Also count scripts that access sensitive input fields (as additional risk indicator)
            const sensitiveScripts = scanData.scripts.filter((s: any) => 
              // Scripts from src URLs that handle forms/sensitive data
              (s.src && (
                s.src.includes('keylogger') ||
                s.src.includes('password') ||
                s.src.includes('creditcard') ||
                // Keystroke tracking services
                s.src.includes('hotjar') ||
                s.src.includes('fullstory') ||
                s.src.includes('logrocket') ||
                s.src.includes('mouseflow')
              )) ||
              // Inline scripts that access sensitive form elements
              (s.content && (
                s.content.includes('input[type="password"]') ||
                s.content.includes('creditcard') ||
                s.content.includes('cvv') ||
                s.content.includes('ssn') ||
                s.content.includes('social-security')
              ))
            );
            // Add suspicious scripts as additional risk
            sensitiveInputs += sensitiveScripts.length;

            // Look for scripts that may read personal data (only suspicious trackers, exclude legitimate analytics)
            const dataScripts = scanData.scripts.filter((s: any) => {
              if (!s.src) return false;
              // Exclude legitimate services
              if (isLegitimateService(s.src)) return false;
              
              // Only count suspicious tracking/analytics scripts
              return (
                s.src.includes('analytics') ||
                s.src.includes('track') ||
                s.src.includes('pixel') ||
                s.src.includes('hotjar') ||
                s.src.includes('mixpanel') ||
                s.src.includes('segment') ||
                s.src.includes('amplitude') ||
                s.src.includes('fullstory') ||
                s.src.includes('logrocket') ||
                s.src.includes('mouseflow')
              );
            });
            personalData += dataScripts.length;

            // Look for potentially malicious or suspicious communication (be more selective)
            const suspiciousScripts = scanData.scripts.filter((s: any) => 
              s.src && (
                // Only very high entropy scripts that are also from suspicious domains
                (s.entropy && s.entropy > 6.0 && s.isThirdParty) ||
                // Scripts with clearly malicious patterns
                s.src.includes('base64') ||
                s.src.includes('eval') ||
                s.src.includes('obfuscate') ||
                // Scripts from known risky hosting platforms (not legitimate CDNs)
                (s.isThirdParty && (
                  s.src.includes('pastebin') ||
                  s.src.includes('bit.ly') ||
                  s.src.includes('tinyurl') ||
                  s.src.includes('raw.githubusercontent') ||
                  s.src.includes('gist.github') ||
                  s.src.includes('t.co') ||
                  s.src.includes('shortened')
                )) ||
                // Inline scripts with very high entropy (potential obfuscation)
                (s.src === 'inline' && s.entropy && s.entropy > 6.2)
              )
            );
            maliciousCommunication += suspiciousScripts.length;

            // Count all third-party scripts for reference
            const allThirdPartyScripts = scanData.scripts.filter((s: any) => {
              if (!s.src) return false;
              try {
                const scriptDomain = new URL(s.src).hostname;
                const scanDomain = new URL(scanData.url || scan.url).hostname;
                return scriptDomain !== scanDomain;
              } catch {
                return s.isThirdParty === true;
              }
            });

            // Count additional pixel tracking scripts (only suspicious ones, exclude legitimate)
            const pixelTrackingScripts = scanData.scripts.filter((s: any) => {
              if (!s.src) return false;
              // Exclude legitimate services
              if (isLegitimateService(s.src)) return false;
              
              // Only count suspicious tracking scripts
              return (
                s.src.includes('pixel') ||
                s.src.includes('track') ||
                s.src.includes('analytics') ||
                s.src.includes('hotjar') ||
                s.src.includes('segment') ||
                s.src.includes('mixpanel') ||
                s.src.includes('amplitude') ||
                s.src.includes('doubleclick') ||
                s.src.includes('googleadservices')
              );
            });
            additionalPixelScripts += pixelTrackingScripts.length;

            // Look for Bluetooth access patterns
            const bluetoothScripts = scanData.scripts.filter((s: any) => 
              s.src && (
                s.src.includes('bluetooth') ||
                s.src.includes('web-bluetooth') ||
                s.src.includes('ble') ||
                (s.containsFunction && s.src.includes('navigator.bluetooth'))
              ) ||
              (s.content && s.content.includes('navigator.bluetooth'))
            );
            bluetoothAccess += bluetoothScripts.length;

            // Look for payment frame patterns (only suspicious/unknown payment processors)
            const paymentScripts = scanData.scripts.filter((s: any) => {
              if (!s.src) {
                // Check inline content for suspicious payment patterns
                if (s.content && (
                  s.content.includes('payment') && 
                  !s.content.includes('stripe') && 
                  !s.content.includes('paypal')
                )) {
                  return true;
                }
                return false;
              }
              
              // Exclude legitimate payment processors
              if (isLegitimateService(s.src)) return false;
              
              // Only count suspicious payment-related scripts
              return (
                // Generic payment terms from unknown domains
                (s.src.includes('payment') && !s.src.includes('stripe') && !s.src.includes('paypal')) ||
                s.src.includes('checkout') ||
                s.src.includes('billing') ||
                s.src.includes('commerce') ||
                // Suspicious payment domains
                (s.src.includes('secure.') && !s.src.includes('authorize.net')) ||
                s.src.includes('payments.') ||
                s.src.includes('pay.')
              );
            });
            paymentFrames += paymentScripts.length;

            // Look for GPS/geolocation access (only actual API calls, not map libraries)
            const gpsScripts = scanData.scripts.filter((s: any) => {
              // Only count scripts that actually call geolocation API
              if (s.content && (
                s.content.includes('navigator.geolocation') ||
                s.content.includes('getCurrentPosition') ||
                s.content.includes('watchPosition')
              )) {
                return true;
              }
              
              // Exclude legitimate map libraries
              if (s.src && isLegitimateService(s.src)) return false;
              
              // Only count if it's actually requesting location (not just displaying maps)
              return s.src && (
                s.src.includes('geolocation') ||
                s.src.includes('location') ||
                s.src.includes('gps')
              ) && !s.src.includes('maps'); // Exclude map display libraries
            });
            gpsLocation += gpsScripts.length;

          }

          // Check for actual threats from scan results
          if (scanData?.threats) {
            maliciousCommunication += scanData.threats.length;
          }

          // Check vulnerabilities for additional risks
          if (scanData?.vulnerabilities) {
            scanData.vulnerabilities.forEach((vuln: string) => {
              const vulnLower = vuln.toLowerCase();
              if (vulnLower.includes('payment') || vulnLower.includes('iframe') || vulnLower.includes('frame')) {
                paymentFrames += 1;
              }
              if (vulnLower.includes('geolocation') || vulnLower.includes('gps') || vulnLower.includes('location')) {
                gpsLocation += 1;
              }
              if (vulnLower.includes('bluetooth')) {
                bluetoothAccess += 1;
              }
            });
          }
        } catch (error) {
          console.error('Error parsing scan data:', error);
        }
      });
    }

    // Combine trackers API count with additional pixel scripts found in scans
    const totalCookieTracking = cookieTracking + additionalPixelScripts;

    // Helper function to determine risk level
    const getRiskLevel = (count: number, thresholds: { low: number; medium: number }): 'low' | 'medium' | 'high' => {
      if (count === 0) return 'low';
      if (count <= thresholds.low) return 'low';
      if (count <= thresholds.medium) return 'medium';
      return 'high';
    };

    const factors: RiskFactor[] = [
        {
          icon: Cookie,
          label: "Cookies and pixel tracking",
          count: totalCookieTracking,
          progress: Math.min(100, totalCookieTracking * 20),
          link: "/trackers",
          riskLevel: getRiskLevel(totalCookieTracking, { low: 5, medium: 15 }),
          description: totalCookieTracking > 0 
            ? "Suspicious tracking scripts detected (excludes legitimate analytics)"
            : "No suspicious tracking detected"
        },
        {
          icon: Shield,
          label: "Running outside your trust",
          count: untrustedScripts,
          progress: Math.min(100, untrustedScripts * 10),
          link: "/scripts",
          riskLevel: getRiskLevel(untrustedScripts, { low: 10, medium: 30 }),
          description: untrustedScripts > 0
            ? "Third-party scripts from unknown domains"
            : "All scripts from trusted sources"
        },
        {
          icon: ExternalLink,
          label: "Loaded from an open CDN",
          count: openCdnScripts,
          progress: Math.min(100, openCdnScripts * 15),
          link: "/scripts",
          riskLevel: getRiskLevel(openCdnScripts, { low: 3, medium: 10 }),
          description: openCdnScripts > 0
            ? "Scripts from untrusted CDNs (excludes trusted CDNs)"
            : "No untrusted CDN scripts"
        },
        {
          icon: Keyboard,
          label: "Accessing sensitive inputs",
          count: sensitiveInputs,
          progress: Math.min(100, sensitiveInputs * 25),
          link: "/inputs",
          riskLevel: getRiskLevel(sensitiveInputs, { low: 2, medium: 5 }),
          description: sensitiveInputs > 0
            ? "Scripts accessing sensitive form fields"
            : "No sensitive input access detected"
        },
        {
          icon: FileText,
          label: "Reading personal data",
          count: personalData,
          progress: Math.min(100, personalData * 30),
          link: "/trackers",
          riskLevel: getRiskLevel(personalData, { low: 3, medium: 10 }),
          description: personalData > 0
            ? "Suspicious data collection scripts (excludes legitimate analytics)"
            : "No suspicious data collection"
        },
        {
          icon: MessageCircle,
          label: "Communicating to malicious",
          count: maliciousCommunication,
          progress: Math.min(100, maliciousCommunication * 20),
          link: "/threat-intelligence",
          riskLevel: getRiskLevel(maliciousCommunication, { low: 1, medium: 3 }),
          description: maliciousCommunication > 0
            ? "High-risk: Suspicious or malicious scripts detected"
            : "No malicious communication detected"
        },
        {
          icon: Bluetooth,
          label: "Accessing Bluetooth",
          count: bluetoothAccess,
          progress: Math.min(100, bluetoothAccess * 50),
          link: "/actions",
          riskLevel: getRiskLevel(bluetoothAccess, { low: 1, medium: 2 }),
          description: bluetoothAccess > 0
            ? "Scripts accessing Bluetooth API"
            : "No Bluetooth access detected"
        },
        {
          icon: CreditCard,
          label: "Running in a payment frame",
          count: paymentFrames,
          progress: Math.min(100, paymentFrames * 50),
          link: "/csp",
          riskLevel: getRiskLevel(paymentFrames, { low: 1, medium: 2 }),
          description: paymentFrames > 0
            ? "Suspicious payment processing (excludes legitimate processors)"
            : "No suspicious payment frames"
        },
        {
          icon: MapPin,
          label: "Requesting GPS location",
          count: gpsLocation,
          progress: Math.min(100, gpsLocation * 50),
          link: "/actions",
          riskLevel: getRiskLevel(gpsLocation, { low: 1, medium: 2 }),
          description: gpsLocation > 0
            ? "Scripts requesting geolocation access"
            : "No GPS location requests"
        },
    ];

    setRiskFactors(factors);
  }, [scans, trackers]);
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
              Risk Factors
            </h3>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
              Potential risks detected (excludes legitimate services). Counts are summed across all your scans.
            </p>
          </div>
          <Link href="/csp">
            <Button variant="link" className="text-purple-600 dark:text-purple-400 text-sm p-0 hover:text-emerald-700 dark:hover:text-emerald-300">
              View Analysis →
            </Button>
          </Link>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {riskFactors.length > 1 && scans?.length > 0 && (
            <p className="text-xs text-slate-500 dark:text-slate-400 -mt-1 mb-2">
              Showing totals across {scans.length} scan{scans.length !== 1 ? 's' : ''}. For per-URL risk, open a specific scan report.
            </p>
          )}
          {riskFactors.map((factor, index) => {
            const getRiskColor = (level?: 'low' | 'medium' | 'high') => {
              if (!level || level === 'low') return 'text-green-600 dark:text-green-400';
              if (level === 'medium') return 'text-yellow-600 dark:text-yellow-400';
              return 'text-red-600 dark:text-red-400';
            };
            
            const getProgressColor = (level?: 'low' | 'medium' | 'high') => {
              if (!level || level === 'low') return 'bg-green-500 group-hover:bg-green-600';
              if (level === 'medium') return 'bg-yellow-500 group-hover:bg-yellow-600';
              return 'bg-red-500 group-hover:bg-red-600';
            };

            return (
              <Link key={index} href={factor.link}>
                <div className="flex items-center justify-between p-2 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer transition-colors group">
                  <div className="flex items-center space-x-3 flex-1">
                    <factor.icon className="w-4 h-4 text-slate-600 dark:text-slate-400 group-hover:text-purple-600 dark:group-hover:text-purple-400 transition-colors" />
                    <div className="flex-1 min-w-0">
                      <span className="text-sm text-slate-900 dark:text-white group-hover:text-emerald-700 dark:group-hover:text-emerald-300 transition-colors block">
                        {factor.label}
                      </span>
                      {factor.description && (
                        <span className="text-xs text-slate-500 dark:text-slate-400 block truncate">
                          {factor.description}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className={`text-sm font-semibold transition-colors ${getRiskColor(factor.riskLevel)}`}>
                      {factor.count}
                    </span>
                    {factor.riskLevel && factor.riskLevel !== 'low' && (
                      <span className={`text-xs px-1.5 py-0.5 rounded ${
                        factor.riskLevel === 'high' 
                          ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                          : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                      }`}>
                        {factor.riskLevel}
                      </span>
                    )}
                    <div className="w-16 h-2 bg-slate-200 dark:bg-slate-700 rounded-full">
                      {factor.progress > 0 && (
                        <div 
                          className={`h-2 rounded-full transition-colors ${getProgressColor(factor.riskLevel)}`}
                          style={{ width: `${factor.progress}%` }}
                        ></div>
                      )}
                    </div>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
